import { z } from 'zod';

import { LocalDateSchema, MyWorkItemSchema, WeekMilestoneSchema } from './my-work.dto';
import { MeAttentionSchema } from './notifications.dto';
import { ProjectSystemKind } from './projects.dto';
import { OnboardingIntentSchema } from './user-prefs.dto';

/**
 * Home v2 and the cross-team "me" summaries (docs/release-1/17-V2-BUILD-PLAN.md
 * §5 W4-0; concept §5.2). Everything is about ME:
 *
 *  - GET /api/me/home?today=     → HomeSummary (My Work guards, P6: it carries
 *                                  my today plan — browser JWT or a PAT with
 *                                  `me.plan:read`; agent PATs and impersonation refused)
 *  - GET /api/me/projects        → MeProjects   (normal `/api/me` guards; replaces the per-team switcher fan-out)
 *  - GET /api/me/unread-summary  → UnreadSummary (normal `/api/me` guards; replaces the per-team bottom-nav calls)
 *
 * Privacy (§2.2): the only private data here is MY OWN today plan
 * (`todayPlan`). Team pulse, attention, owner alerts, My week and projects
 * are computed from public task / milestone facts with the shared metric
 * definitions (§2.3) — never from anyone's plan, not even a count (P11).
 * Home shows at most 5 blocks; which ones depends on my role per team (§2.5).
 */

export const HomeQuerySchema = z.object({ today: LocalDateSchema }).strict();
export type HomeQuery = z.infer<typeof HomeQuerySchema>;

/** Block 1 — Morning card: my plan for today ("2/5 · 1h40 of 6h"). Mine only. */
export const HomeTodayPlanSchema = z.object({
  /** In Today, Open and not privately done (= My Work `counts.todayRemaining`). */
  remaining: z.number().int().min(0),
  /** Done today, privately or in the team (= `counts.todayDone`). */
  done: z.number().int().min(0),
  /** Σ estimateMinutes over today's plan, done rows included (as on the Week board). */
  plannedMinutes: z.number().int().min(0),
  /** Work prefs `dailyCapacityMinutes`. */
  capacityMinutes: z.number().int().min(0),
  /** = `counts.carriedOver`. */
  carriedOver: z.number().int().min(0),
  /** The first ≤ 5 rows of My Work › Today (rank order) — rendered with the same TaskRow. */
  top: z.array(MyWorkItemSchema).max(5),
});
export type HomeTodayPlan = z.infer<typeof HomeTodayPlanSchema>;

/** Owner-only, actionable limits (seats, storage > 80 %, AI cap, `past_due`) — Team owners only. */
export const OwnerAlertKindSchema = z.enum(['seats', 'storage', 'ai_cap', 'past_due']);
export type OwnerAlertKind = z.infer<typeof OwnerAlertKindSchema>;

export const OwnerAlertSchema = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  kind: OwnerAlertKindSchema,
  /** Ready-to-show copy, e.g. "Acme uses 9 of 10 seats". */
  message: z.string(),
  /** In-app path to fix it, e.g. `/teams/acme/settings/billing`. */
  href: z.string(),
});
export type OwnerAlert = z.infer<typeof OwnerAlertSchema>;

/** Block 3 — one day of "My week": my deadlines (my set, §2.3 Mine) + milestones (all teams + Personal). */
export const HomeWeekDaySchema = z.object({
  date: LocalDateSchema,
  deadlines: z.array(MyWorkItemSchema),
  milestones: z.array(WeekMilestoneSchema),
});
export type HomeWeekDay = z.infer<typeof HomeWeekDaySchema>;

/** The roles that get a Team pulse card (Lead and above, per team — §2.5). */
export const TeamPulseRoleSchema = z.enum(['OWNER', 'MANAGER', 'LEAD']);

/** Block 4 — one card per team where MY role is Lead+. Public task facts only (P11). */
export const TeamPulseSchema = z.object({
  teamId: z.string().uuid(),
  teamSlug: z.string(),
  teamName: z.string(),
  role: TeamPulseRoleSchema,
  open: z.number().int().min(0),
  overdue: z.number().int().min(0),
  dueThisWeek: z.number().int().min(0),
  blocked: z.number().int().min(0),
  atRiskMilestones: z.number().int().min(0),
  /** "Waiting on others (N)": my delegated tasks in this team (`isDelegated`). */
  waitingOnOthers: z.number().int().min(0),
});
export type TeamPulse = z.infer<typeof TeamPulseSchema>;

/** Block 5 — only when every regular membership is Guest (VIEWER). */
export const ProjectIFollowSchema = z.object({
  projectId: z.string().uuid(),
  slug: z.string(),
  teamSlug: z.string(),
  name: z.string(),
  teamName: z.string(),
  progressPct: z.number().min(0).max(100),
  nextMilestone: z.object({ name: z.string(), date: LocalDateSchema }).nullable(),
  /** Tasks that entered Done this calendar week (`isDoneInRange`). */
  doneThisWeek: z.number().int().min(0),
});
export type ProjectIFollow = z.infer<typeof ProjectIFollowSchema>;

/** Show onboarding (server state in work prefs `onboarding`). */
export const HomeFirstRunSchema = z.object({
  show: z.boolean(),
  intent: OnboardingIntentSchema.nullable(),
});
export type HomeFirstRun = z.infer<typeof HomeFirstRunSchema>;

/**
 * GET /api/me/home?today= — one call for the whole Home screen.
 * Empty arrays mean "don't render the block" (no empty Team pulse for a solo user).
 */
export const HomeSummarySchema = z.object({
  todayPlan: HomeTodayPlanSchema,
  /** Block 2 — attention chips (mentions · to review · approvals · invitations …). */
  attention: MeAttentionSchema,
  /** Block 2 — owner-only limit alerts; [] unless I own a team with something actionable. */
  ownerAlerts: z.array(OwnerAlertSchema),
  /** Block 3 — 7 consecutive days starting `today`. */
  myWeek: z.array(HomeWeekDaySchema).max(7),
  /** Block 4 — [] when I'm not Lead+ in any regular team. */
  teamPulse: z.array(TeamPulseSchema),
  /** Block 5 — [] unless every regular membership I have is Guest. */
  projectsIFollow: z.array(ProjectIFollowSchema),
  firstRun: HomeFirstRunSchema,
});
export type HomeSummary = z.infer<typeof HomeSummarySchema>;

// ── GET /api/me/projects ────────────────────────────────────────────────────

/**
 * Project health from public facts (§2.3): `overdue` = any Overdue task;
 * `at_risk` = `isProjectAtRisk`; `ok` otherwise; null when unknown (archived
 * or no open work).
 */
export const ProjectHealthSchema = z.enum(['ok', 'at_risk', 'overdue']);
export type ProjectHealth = z.infer<typeof ProjectHealthSchema>;

/** Every project I can open, across all my teams + Personal, in one call. */
export const MeProjectSchema = z.object({
  project: z.object({
    id: z.string().uuid(),
    slug: z.string(),
    key: z.string(),
    name: z.string(),
    color: z.string().nullable(),
    /** The project's icon (Lucide name / short token), like `ProjectSummary.icon`; null = none.
     *  Optional only so older fixtures stay valid — the server always sends it. */
    icon: z.string().nullable().optional(),
    /** 'inbox' = the Personal capture project — never listed as a project (`isNavigableProject`). */
    systemKind: ProjectSystemKind.nullable(),
    /** External project link (W6-E, 0122) — only ever set on my Personal projects; null = none.
     *  Optional only so older fixtures stay valid — the server always sends it. */
    externalUrl: z.string().nullable().optional(),
  }),
  team: z.object({
    id: z.string().uuid(),
    slug: z.string(),
    /** Raw name; show `personalTeamLabel(team)` ("Personal") for kind='personal'. */
    name: z.string(),
    kind: z.enum(['personal', 'regular']),
  }),
  health: ProjectHealthSchema.nullable(),
  /** Pinned to Favorites (`pinned_items`). */
  isFavorite: z.boolean(),
  /**
   * When I last opened it, if the server knows; null otherwise. The order of
   * "Recent" is work prefs `recentProjectIds` (newest first).
   */
  lastVisitedAt: z.string().nullable(),
});
export type MeProject = z.infer<typeof MeProjectSchema>;

export const MeProjectsSchema = z.array(MeProjectSchema);
export type MeProjects = z.infer<typeof MeProjectsSchema>;

// ── GET /api/me/unread-summary ──────────────────────────────────────────────

/** All my unread counters in one call (bottom nav, sidebar, Projects tab dots). */
export const UnreadSummarySchema = z.object({
  /** Unread Inbox notifications. */
  inbox: z.number().int().min(0),
  chat: z.object({
    /** Unread direct messages (message count, like the old badge). */
    dms: z.number().int().min(0),
    /** Unread channel messages across my teams (message count; muted channels excluded). */
    channels: z.number().int().min(0),
    /** Unread @mentions of me in chat. */
    mentions: z.number().int().min(0),
  }),
  /** Unread project activity for me, keyed by project id; projects with 0 are omitted. */
  projects: z.record(z.string().uuid(), z.number().int().min(0)),
});
export type UnreadSummary = z.infer<typeof UnreadSummarySchema>;
